'use client';

import dayjs from 'dayjs';

import { ChevronIcon, SecondaryButton } from '@web/ui';

import styles from './RetainerCycleSwitcher.module.scss';

import type { RetainerCycle } from '@/features/jobs/types';

type RetainerCycleSwitcherProps = {
  cycles: RetainerCycle[];
  selectedCycleId: string | null;
  onChange: (cycleId: string) => void;
};

function formatCycleLabel(cycle: RetainerCycle): string {
  const startsOn = dayjs(cycle.starts_on);
  if (startsOn.isValid()) {
    return startsOn.format('MMM YYYY');
  }

  return cycle.cycle_key;
}

export function RetainerCycleSwitcher({
  cycles,
  selectedCycleId,
  onChange,
}: RetainerCycleSwitcherProps) {
  if (cycles.length === 0) {
    return null;
  }

  const selectedIndex = Math.max(
    0,
    cycles.findIndex((cycle) => cycle.id === selectedCycleId),
  );
  const activeCycle = cycles[selectedIndex] ?? cycles[0];
  const canGoPrevious = selectedIndex > 0;
  const canGoNext = selectedIndex < cycles.length - 1;

  return (
    <div className={styles.retainerCycleSwitcher}>
      <SecondaryButton
        ariaLabel="Previous cycle"
        variant="mini"
        disabled={!canGoPrevious}
        onClick={() => {
          if (!canGoPrevious) {
            return;
          }
          onChange(cycles[selectedIndex - 1].id);
        }}
      >
        <span
          className={`${styles.retainerCycleChevron} ${styles.retainerCycleChevronLeft}`}
        >
          <ChevronIcon direction="right" />
        </span>
      </SecondaryButton>

      <div className={styles.retainerCycleSummary}>
        <p className={styles.retainerCycleValue}>
          {formatCycleLabel(activeCycle)}
        </p>
      </div>

      <SecondaryButton
        ariaLabel="Next cycle"
        variant="mini"
        disabled={!canGoNext}
        onClick={() => {
          if (!canGoNext) {
            return;
          }
          onChange(cycles[selectedIndex + 1].id);
        }}
      >
        <span className={styles.retainerCycleChevron}>
          <ChevronIcon direction="right" />
        </span>
      </SecondaryButton>
    </div>
  );
}
